import type { LucideIcon } from 'lucide-react';
import { ArrowRight, Check, Clock, Minus, PhoneCall, X } from 'lucide-react';

/** The 9 canonical presence states. Never invent a tenth. */
export type PresenceState =
  | 'available'
  | 'busy'
  | 'dnd'
  | 'brb'
  | 'away'
  | 'offline'
  | 'appearOffline'
  | 'oof'
  | 'inAMeeting';

export interface PresenceMeta {
  /** Text shown in menus and next to a name. */
  label: string;
  /**
   * Colour classes for the badge. Filled states carry `bg-*`; hollow states
   * carry `border-* text-*` so the outline and the glyph share one colour.
   */
  dotClass: string;
  /** Glyph rendered inside the dot at every size. `null` = solid fill only. */
  glyph: LucideIcon | null;
  /** Outlined circle on the surface colour instead of a filled dot (offline family). */
  hollow: boolean;
  /** Whether the user is reachable for calls — drives call-button state. */
  reachable: boolean;
}

/**
 * Glyph + colour per state, matched to the Microsoft Teams vocabulary:
 * tick = available, solid = busy, dash = do-not-disturb, clock = away /
 * be-right-back, outlined cross = offline, arrow = out of office.
 * Presence is never colour-only — every state also carries `label`, which
 * `PresenceDot` publishes as its `aria-label` and `title`.
 */
export const PRESENCE_META: Record<PresenceState, PresenceMeta> = {
  available: { label: 'Available', dotClass: 'bg-presence-available', glyph: Check, hollow: false, reachable: true },
  busy: { label: 'Busy', dotClass: 'bg-presence-busy', glyph: null, hollow: false, reachable: true },
  dnd: { label: 'Do not disturb', dotClass: 'bg-presence-dnd', glyph: Minus, hollow: false, reachable: false },
  brb: { label: 'Be right back', dotClass: 'bg-presence-brb', glyph: Clock, hollow: false, reachable: true },
  away: { label: 'Away', dotClass: 'bg-presence-away', glyph: Clock, hollow: false, reachable: true },
  offline: { label: 'Offline', dotClass: 'border-presence-offline text-presence-offline', glyph: X, hollow: true, reachable: false },
  appearOffline: { label: 'Appear offline', dotClass: 'border-presence-appear-offline text-presence-appear-offline', glyph: X, hollow: true, reachable: false },
  oof: { label: 'Out of office', dotClass: 'bg-presence-oof', glyph: ArrowRight, hollow: false, reachable: false },
  inAMeeting: { label: 'In a meeting', dotClass: 'bg-presence-meeting', glyph: null, hollow: false, reachable: false },
};

/** Icon used when a presence state should be shown inline in a call context. */
export const PRESENCE_CALL_ICON: LucideIcon = PhoneCall;

/** Ordered list for presence pickers. */
export const PRESENCE_ORDER: readonly PresenceState[] = [
  'available',
  'busy',
  'dnd',
  'brb',
  'away',
  'appearOffline',
] as const;
